import { clamp } from 'lodash';
import { Decoration_Type } from 'ricos-schema';
import fontSizeDataDefaults from 'ricos-schema/dist/statics/font_size.defaults.json';
import type { DOMOutputSpec } from 'ricos-types';
import { createRicosMarkExtension } from 'wix-rich-content-plugin-commons';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    fontSize: {
      /**
       * Set a font mark
       */
      setFontSize: (size: number) => ReturnType;
    };
  }
}

export const fontSize = createRicosMarkExtension({
  type: 'mark',
  groups: [],
  name: Decoration_Type.FONT_SIZE,
  createExtensionConfig({ mergeAttributes }) {
    return {
      name: this.name,
      addAttributes() {
        return fontSizeDataDefaults;
      },

      renderHTML({ HTMLAttributes }) {
        const htmlAttrs = {
          style: `font-size: ${HTMLAttributes.value}${HTMLAttributes.unit.toLowerCase()}`,
          'data-font-size': HTMLAttributes.value,
        };
        return ['span', mergeAttributes(htmlAttrs), 0] as DOMOutputSpec;
      },
      parseHTML() {
        // Like in draft we parse only internal copy/pasted fontSize values
        return [
          {
            tag: 'span',
            getAttrs: (element: HTMLElement) => {
              const attribute = element.attributes.getNamedItem('data-font-size');
              if (!isValidSizeValue(attribute?.value)) return false;
              return {
                value: clamp(1, Number(attribute?.value), 900),
                unit: 'PX',
              };
            },
          },
        ];
      },
      addCommands() {
        return {
          setFontSize:
            (size: number) =>
            ({ commands }) => {
              if (!isValidSizeValue(size)) {
                return false;
              }

              return commands.setMark(this.name, {
                value: size,
                unit: 'PX',
              });
            },
        };
      },
    };
  },
});

const isValidSizeValue = (size: unknown): boolean => {
  const n = Number(size);
  return Number.isInteger(n) && n > 0 && n <= 900;
};
